import { Box, Button, Icon, Modal, TextInput } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { HOLD_QUEUE_PATH } from './HoldMotionTabs';

const CONFIRMATION_TEXT = 'Release All Projects';

interface DisableProgramHoldMotionModalProps {
  isOpen: boolean;
  programLabel: string;
  heldCount: number;
  isLoading: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
}

export const DisableProgramHoldMotionModal = ({
  isOpen,
  programLabel,
  heldCount,
  isLoading,
  handleClose,
  handleConfirm,
}: DisableProgramHoldMotionModalProps) => {
  const [releaseTextConfirmation, setReleaseTextConfirmation] = useState('');
  const hasHeldProjects = heldCount > 0;

  // The modal instance is shared across program rows, so a phrase typed for one program must not
  // carry over and pre-confirm the next.
  useEffect(() => {
    setReleaseTextConfirmation('');
  }, [isOpen, programLabel]);

  const handleConfirmationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setReleaseTextConfirmation(event.target.value);
  }, []);

  const isConfirmDisabled =
    isLoading || (hasHeldProjects && releaseTextConfirmation.toLowerCase() !== CONFIRMATION_TEXT.toLowerCase());

  return (
    <Modal isOpen={isOpen} onDismiss={handleClose} maxWidth="4xl" ariaLabelledBy="disableProgramHoldMotionModal">
      <Box direction="column" gap="md" data-testid="disable-program-hold-motion-modal" margin="lg">
        {hasHeldProjects && (
          <Box
            width="50px"
            height="50px"
            radius="circle"
            background="danger-100"
            borderWidth="2px"
            borderColor="grey-600"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box width="27px" height="27px" alignItems="center" justifyContent="center">
              <Icon name="t-warning" size="xl" color="danger-400" />
            </Box>
          </Box>
        )}
        <Box color="grey-700" fontWeight="bold" fontSize="xl" id="disableProgramHoldMotionModal">
          Disable Hold Motion for {programLabel}?
        </Box>
        {hasHeldProjects ? (
          <>
            <Box display="block">
              Disabling Hold Motion for {programLabel} will{' '}
              <strong>release all {heldCount} held projects at once</strong>. Approval notifications will be sent to
              the respective EPCs and approvals will enter the payout batching pipeline.
            </Box>
            <Box direction="row" justifyContent="space-between" background="grey-50" radius="md" padding="md">
              <Box>Projects to release</Box>
              <Box fontSize="lg" fontWeight="bold">
                {heldCount}
              </Box>
            </Box>
            <Box
              display="block"
              radius="md"
              borderWidth="xs"
              borderColor="danger-400"
              color="danger-600"
              padding="md"
              background="danger-100"
            >
              <strong>Warning:</strong> If you want to release in smaller batches, keep Hold Motion enabled and use
              the{' '}
              <Link to={HOLD_QUEUE_PATH} target="_blank" rel="noopener noreferrer">
                Hold Queue
              </Link>{' '}
              to select and release projects manually first.
            </Box>
            <Box gap="md">
              <Box display="block">
                Write the following text in the box below to release all projects:{' '}
                <strong>{CONFIRMATION_TEXT}</strong>
              </Box>
              <TextInput
                id="confirmDisableProgramHoldMotion"
                name="confirmDisableProgramHoldMotion"
                label=""
                placeholder={`Type '${CONFIRMATION_TEXT}' to confirm`}
                value={releaseTextConfirmation}
                onChange={handleConfirmationChange}
                isRequired
              />
            </Box>
          </>
        ) : (
          <Box display="block">
            No projects are currently held for {programLabel}, so nothing will be released. New approvals for this
            program will stop holding.
          </Box>
        )}
        <Box direction="row" gap="md" justifyContent="flex-end">
          <Button variant="secondary" tone="neutral" type="button" onClick={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button tone="danger" type="button" onClick={handleConfirm} isDisabled={isConfirmDisabled}>
            {hasHeldProjects ? 'Release All and Disable' : 'Disable Hold Motion'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
